import { Dispatch, SetStateAction } from "react";

export type ID = undefined | null | number | string;

export type PaginationState = {
  page: number;
  limit: 10 | 30 | 50 | 100;
  totalDocs?: number;
  totalPages?: number;
  pagingCounter?: number;
  hasPrevPage?: boolean;
  hasNextPage?: boolean;
  prevPage?: string | null;
  nextPage?: string | null;
};

export type SortState = {
  sort?: string;
  order?: "asc" | "desc";
};

export type FilterState = {
  filter?: unknown;
};

export type SearchState = {
  search?: string;
};

export type Response<T> = {
  data?: T;
  payload?: {
    message?: string;
    errors?: {
      [key: string]: Array<string>;
    };
    pagination?: PaginationState;
  };
};

export type QueryState = PaginationState &
  SortState &
  FilterState &
  SearchState;

export type QueryRequestContextProps = {
  state: QueryState;
  updateState: (updates: Partial<QueryState>) => void;
};

export const initialQueryState: QueryState = {
  page: 1,
  limit: 10,
};

export const initialQueryRequest: QueryRequestContextProps = {
  state: initialQueryState,
  updateState: () => {},
};

export type QueryResponseContextProps<T> = {
  response?: ResponseTemporary<T> | undefined;
  refetch: () => void;
  isLoading: boolean;
  query: string;
};

export type QueryResponseContextNonPaginationProps<T> = {
  response?: ResponseTemporaryNonPagination<T> | undefined;
  refetch: () => void;
  isLoading: boolean;
  query: string;
};

export const initialQueryResponse = {
  refetch: () => {},
  isLoading: false,
  query: "",
};

export type ListViewContextProps = {
  selected: Array<ID>;
  onSelect: (selectedId: ID) => void;
  onSelectAll: () => void;
  clearSelected: () => void;
  // NULL => (CREATION MODE) | MODAL IS OPENED
  // NUMBER => (EDIT MODE) | MODAL IS OPENED
  // UNDEFINED => MODAL IS CLOSED
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  isReject: boolean;
  setIsReject: Dispatch<SetStateAction<boolean>>;
  isAllSelected: boolean;
  disabled: boolean;
  totalAmountSelected?: number;
  totalAmountSelectedDebit?: number;
  totalAmountSelectedCredit?: number;
};

export type ListViewContextAlternativeProps = {
  selected: Array<ID>;
  onSelect: (selectedId: ID) => void;
  onSelectAll: () => void;
  clearSelected: () => void;
  // NULL => (CREATION MODE) | MODAL IS OPENED
  // NUMBER => (EDIT MODE) | MODAL IS OPENED
  // UNDEFINED => MODAL IS CLOSED
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  isReject: boolean;
  emailConfirmation: string;
  merchantId: string;
  setIsReject: Dispatch<SetStateAction<boolean>>;
  setEmailConfirmation: Dispatch<SetStateAction<string>>;
  setMerchantId: Dispatch<SetStateAction<string>>;
  isAllSelected: boolean;
  disabled: boolean;
  totalAmountSelected: number;
};

export const initialListView: ListViewContextProps = {
  selected: [],
  showModal: false,
  isReject: false,
  onSelect: () => {},
  onSelectAll: () => {},
  clearSelected: () => {},
  setShowModal: () => {},
  setIsReject: () => {},
  isAllSelected: false,
  disabled: false,
  totalAmountSelected: 0,
};

export const initialListViewAlternative: ListViewContextAlternativeProps = {
  selected: [],
  showModal: false,
  isReject: false,
  emailConfirmation: "admin@mcpayment.id",
  merchantId: "",
  onSelect: () => {},
  onSelectAll: () => {},
  clearSelected: () => {},
  setShowModal: () => {},
  setIsReject: () => {},
  setEmailConfirmation: () => {},
  setMerchantId: () => {},
  isAllSelected: false,
  disabled: false,
  totalAmountSelected: 0,
};

/**
 * You Can Remove Code Above If You Start Implement New API
 * The Code Above Is Temporary For Mock
 */

export type PaginationStateTemporary<T> = {
  docs?: T;
  totalDocs?: number;
  limit: number;
  page: number;
  totalPages: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: string | null;
  nextPage: string | null;
};

export type SortStateTemporary = {
  sort?: string;
  order?: "asc" | "desc";
};

export type FilterStateTemporary = {
  filter?: unknown;
};

export type SearchStateTemporary = {
  search?: string;
};

export type ResponseTemporary<T> = {
  responseCode?: string;
  responseMessage?: string;
  detailData: PaginationStateTemporary<T>;
};
export type ResponseTemporaryNonPagination<T> = {
  responseCode?: string;
  responseMessage?: string;
  detailData: T;
};

export type QueryStateTemporary = PaginationState &
  SortState &
  FilterState &
  SearchState;

export type QueryRequestContextPropsTemporary = {
  state: QueryStateTemporary;
  updateState: (updates: Partial<QueryStateTemporary>) => void;
};

export const initialQueryStateTemporary: QueryState = {
  page: 1,
  limit: 10,
};

export const initialQueryRequestTemporary: QueryRequestContextPropsTemporary = {
  state: initialQueryStateTemporary,
  updateState: () => {},
};

export type QueryResponseContextPropsTemporary<T> = {
  response?: Response<Array<T>> | undefined;
  refetch: () => void;
  isLoading: boolean;
  query: string;
};

export const initialQueryResponseTemporary = {
  refetch: () => {},
  isLoading: false,
  query: "",
};
